import React, { useState, useEffect, useCallback } from "react";
import { Save, Loader2, Package as PackageIcon } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FeatureCategoryBadge } from "./FeatureBadges";

const PACKAGES = ["STARTER", "GROWTH", "ENTERPRISE"];

const PACKAGE_STYLE = {
  STARTER: { grad: "from-sky-500/20 to-transparent", accent: "text-sky-300", ring: "ring-sky-500/30" },
  GROWTH: { grad: "from-violet-500/20 to-transparent", accent: "text-violet-300", ring: "ring-violet-500/30" },
  ENTERPRISE: { grad: "from-amber-500/20 to-transparent", accent: "text-amber-300", ring: "ring-amber-500/30" },
};

export default function PackageMappingTab() {
  const [selectedPkg, setSelectedPkg] = useState("STARTER");
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, pkg] = await Promise.all([
        api.get("/platform/features"),
        api.get(`/platform/packages/${selectedPkg}/features`),
      ]);
      setCatalog(cat.data.items || []);
      setSelected(new Set(pkg.data.features || []));
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, [selectedPkg]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/platform/packages/${selectedPkg}/features`, { features: Array.from(selected) });
      toast.success(`Mapping ${selectedPkg} disimpan (${selected.size} feature).`);
    } catch (e) { notifyError(e); } finally { setSaving(false); }
  };

  const grouped = catalog.reduce((acc, f) => {
    const c = f.feature_category || "other";
    (acc[c] = acc[c] || []).push(f);
    return acc;
  }, {});

  return (
    <div data-testid="tab-package-mapping">
      {/* Package Selector */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {PACKAGES.map((p) => {
          const st = PACKAGE_STYLE[p];
          const isActive = selectedPkg === p;
          return (
            <button key={p} onClick={() => setSelectedPkg(p)}
              className={`text-left p-4 rounded-2xl border transition-all bg-gradient-to-br ${st.grad} ${
                isActive ? `bg-white/10 border-white ring-2 ${st.ring}` : "border-white/10 hover:border-white/30"
              }`}
              data-testid={`package-tab-${p.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-1">
                <PackageIcon size={14} className={st.accent} />
                <span className={`text-xs font-black uppercase tracking-wider ${st.accent}`}>{p}</span>
              </div>
              <div className="text-sm text-white/70">Feature mapping paket {p}</div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <div className="text-xs text-white/50">Terpilih: <span className="text-white font-bold" data-testid="package-selected-count">{selected.size}</span> / {catalog.length}</div>
        <Button onClick={save} disabled={saving || loading} className="ml-auto bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40" data-testid="btn-save-package-mapping">
          {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Menyimpan…</> : <><Save size={14} className="mr-1" /> Simpan Mapping</>}
        </Button>
      </div>

      {loading ? (
        <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).map(([cat, feats]) => (
            <div key={cat} className="rounded-2xl border border-white/10 overflow-hidden">
              <div className="px-4 py-2 bg-white/5 flex items-center gap-2">
                <FeatureCategoryBadge category={cat} />
              </div>
              {feats.map((f) => {
                const isChecked = selected.has(f.feature_key);
                return (
                  <label key={f.feature_key} className="px-4 py-3 border-t border-white/5 flex items-center gap-3 cursor-pointer hover:bg-white/5"
                    data-testid={`package-feature-${f.feature_key}`}>
                    <input type="checkbox" checked={isChecked} onChange={() => toggle(f.feature_key)}
                      className="w-4 h-4 accent-emerald-500" data-testid={`checkbox-${f.feature_key}`} />
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-xs text-white/70">{f.feature_key}</div>
                      <div className="text-sm text-white/90">{f.feature_name}</div>
                    </div>
                    {!f.is_active && <span className="text-[10px] text-rose-400 font-bold">INACTIVE</span>}
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
